import { DataGridProps, GridPaginationMeta, GridPaginationModel } from '@mui/x-data-grid'
import { useCallback, useMemo, useRef } from 'react'

export interface PaginationParams {
  offset: number
  limit: number
}

export const usePaginationParams = (
  data: unknown[],
  dataPending: boolean,
  { offset, limit }: PaginationParams,
  onChange: (params: PaginationParams) => void,
): Partial<DataGridProps> => {
  const paginationMetaRef = useRef<GridPaginationMeta>({ hasNextPage: dataPending || data.length === limit })

  const paginationMeta = useMemo(() => {
    const hasNextPage = dataPending || data.length === limit
    if (paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage }
    }
    return paginationMetaRef.current
  }, [dataPending, data.length, limit])

  const onPaginationModelChange = useCallback(
    ({ page, pageSize }: GridPaginationModel) => {
      onChange({ offset: page * pageSize, limit: pageSize })
    },
    [onChange],
  )
  return useMemo<Partial<DataGridProps>>(
    () => ({
      pagination: true,
      paginationMode: 'server',
      loading: dataPending,
      paginationMeta,
      rowCount: paginationMeta.hasNextPage
        ? (Math.floor(offset / limit) + 2) * limit // Estimate total for next page
        : offset + data.length, // Exact count when on last page
      paginationModel: {
        pageSize: limit,
        page: Math.floor(offset / limit),
      },
      onPaginationModelChange,
    }),
    [dataPending, paginationMeta, offset, limit, data.length, onPaginationModelChange],
  )
}
